"use client";

import { motion, MotionConfig, useReducedMotion, type Transition } from "motion/react";
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useMemo,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { DURATION_QUICK, EASE_OUT, SPRING_LAYOUT } from "../../lib/ease";
import { cn } from "../../lib/utils";

export type TabsVariant = "pill" | "underline" | "segment";

type Ctx = {
  value: string;
  setValue: (v: string) => void;
  layoutId: string;
  variant: TabsVariant;
  tabId: (v: string) => string;
  panelId: (v: string) => string;
};

const TabsCtx = createContext<Ctx | null>(null);

function useTabs() {
  const ctx = useContext(TabsCtx);
  if (!ctx) throw new Error("Tabs.* must be used inside <Tabs>");
  return ctx;
}

// Shared shared-layout spring, so the active-tab indicator glides with the same
// physics as the other v2 pills and morphing panels.
const transition: Transition = SPRING_LAYOUT;

export function Tabs({
  defaultValue,
  value,
  onValueChange,
  variant = "pill",
  children,
  className,
  transition: transitionProp,
}: {
  defaultValue?: string;
  value?: string;
  onValueChange?: (v: string) => void;
  variant?: TabsVariant;
  children: ReactNode;
  className?: string;
  /**
   * Overrides the indicator spring. A sidebar being resized passes a zero
   * duration: the indicator re-projects every frame as the container width
   * changes, and springing each of those frames reads as lag.
   */
  transition?: Transition;
}) {
  const [internal, setInternal] = useState(defaultValue ?? "");
  const layoutId = useId();
  const reduce = useReducedMotion();
  const controlled = value !== undefined;
  const current = controlled ? value : internal;
  const setValue = useCallback(
    (v: string) => {
      if (!controlled) setInternal(v);
      onValueChange?.(v);
    },
    [controlled, onValueChange],
  );
  const baseId = useId();
  const tabId = useCallback((v: string) => `${baseId}-tab-${v}`, [baseId]);
  const panelId = useCallback((v: string) => `${baseId}-panel-${v}`, [baseId]);
  const contextValue = useMemo(
    () => ({ value: current, setValue, layoutId, variant, tabId, panelId }),
    [current, layoutId, setValue, variant, tabId, panelId],
  );
  return (
    <MotionConfig transition={reduce ? { duration: 0 } : (transitionProp ?? transition)}>
      <TabsCtx.Provider value={contextValue}>
        {/* layoutRoot: the indicator's layoutId measures in page coordinates, so
            inside fixed/scrolled containers it would replay scroll offsets as
            movement. The pill only ever travels within the list, so scoping
            projection to the Tabs wrapper is always correct. */}
        <motion.div layoutRoot className={className}>
          {children}
        </motion.div>
      </TabsCtx.Provider>
    </MotionConfig>
  );
}

const listClasses: Record<TabsVariant, string> = {
  pill: "inline-flex items-center gap-1 rounded-full bg-v2-card p-1",
  underline: "inline-flex items-center gap-1 border-b border-v2-border",
  // A segmented control reads as a recessed track with a raised active pill, so
  // the track is muted rather than card-coloured.
  segment:
    "inline-flex items-center gap-0.5 rounded-v2-lg border border-v2-border/60 bg-v2-muted p-0.5",
};

/**
 * Per-variant trigger sizing, all on the v2 type scale and the 24/28/32/36/40
 * control ladder. The segmented control is the densest of the three: a 32px
 * row at the chrome size, against the reading size pill and underline use.
 */
const triggerSizeClasses: Record<TabsVariant, string> = {
  pill: "h-8 px-3.5 text-v2-base",
  underline: "px-3.5 py-1.5 text-v2-base",
  segment: "h-8 w-full gap-1.5 px-2 text-v2-sm",
};

/**
 * The positioning wrapper. Segments share the track evenly, so the stretch has
 * to live here — the trigger inside cannot grow past a wrapper sized to content.
 */
const triggerWrapClasses: Record<TabsVariant, string> = {
  pill: "relative",
  underline: "relative",
  segment: "relative min-w-0 flex-1",
};

/**
 * The travelling indicator behind the active trigger.
 *
 * `pill` and `underline` mark the selection with the foreground colour; the
 * segmented control instead lifts a surface-coloured pill out of its muted
 * track, which is why its label stays `foreground` rather than inverting.
 */
const indicatorClasses: Record<TabsVariant, string> = {
  pill: "bg-v2-primary",
  underline: "bg-v2-primary",
  segment: "bg-v2-background shadow-sm",
};

/**
 * What the focus ring's 2px offset paints against.
 *
 * The offset band falls just outside the trigger's box, which puts it inside
 * the list's own padding — so the colour that has to match is the track's, not
 * the page's. A consumer that recolours the track (the app sidebar does) has to
 * override this alongside it.
 */
const triggerRingOffsetClasses: Record<TabsVariant, string> = {
  pill: "focus-visible:ring-offset-v2-card",
  underline: "focus-visible:ring-offset-v2-background",
  segment: "focus-visible:ring-offset-v2-muted",
};

const activeLabelClasses: Record<TabsVariant, string> = {
  pill: "text-v2-primary-foreground",
  underline: "text-v2-foreground",
  segment: "text-v2-foreground",
};

/**
 * Roving focus across the tab strip.
 *
 * Trigger order is read from the DOM rather than a registry, so it can never
 * drift from what is rendered. The pill/segment variants wrap each trigger in a
 * positioning div, so the query has to run against the list, not children.
 *
 * Module scope because it closes over nothing — everything it needs arrives on
 * the event.
 */
const onTabListKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
  if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(event.key)) return;
  event.preventDefault();

  const tabs = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="tab"]'));
  if (tabs.length === 0) return;
  const index = tabs.findIndex(tab => tab.getAttribute("aria-selected") === "true");
  const next =
    event.key === "Home"
      ? 0
      : event.key === "End"
        ? tabs.length - 1
        : event.key === "ArrowRight"
          ? (index + 1) % tabs.length
          : (index - 1 + tabs.length) % tabs.length;

  tabs[next]?.focus();
  tabs[next]?.click();
};

export function TabsList({ children, className }: { children: ReactNode; className?: string }) {
  const { variant } = useTabs();

  return (
    <div
      role="tablist"
      onKeyDown={onTabListKeyDown}
      className={cn(listClasses[variant], className)}
    >
      {children}
    </div>
  );
}

export function TabsTrigger({
  value,
  children,
  className,
  indicatorClassName,
}: {
  value: string;
  children: ReactNode;
  className?: string;
  indicatorClassName?: string;
}) {
  const { value: current, setValue, layoutId, variant, tabId, panelId } = useTabs();
  const active = current === value;

  if (variant === "underline") {
    return (
      <button
        type="button"
        role="tab"
        id={tabId(value)}
        aria-selected={active}
        aria-controls={panelId(value)}
        tabIndex={active ? 0 : -1}
        onClick={() => setValue(value)}
        className={cn(
          "relative isolate -mb-px inline-flex min-h-11 items-center px-3 pb-2.5 pt-1 text-v2-base font-medium transition-colors [&>svg]:shrink-0",
          "outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring focus-visible:ring-offset-2",
          triggerRingOffsetClasses[variant],
          active ? "text-v2-foreground" : "text-v2-muted-foreground hover:text-v2-foreground",
          className,
        )}
      >
        {children}
        {active ? (
        <motion.span
          layoutId={layoutId}
          className={cn(
            "absolute -bottom-px left-0 right-0 h-px bg-v2-primary",
            indicatorClassName,
          )}
        />
        ) : null}
      </button>
    );
  }

  const radius = variant === "pill" ? "rounded-full" : "rounded-v2-md";

  return (
    <div className={triggerWrapClasses[variant]}>
      {active ? (
        <motion.span
          layoutId={layoutId}
          // Numeric so motion can interpolate it during the layout glide. Kept
          // in step with `radius` below — 6px matches rounded-v2-md, which is
          // the correct inner radius for a pill inset in a rounded-v2-lg track.
          style={{ borderRadius: variant === "pill" ? 9999 : 6 }}
          className={cn(
            "absolute inset-0",
            indicatorClasses[variant],
            radius,
            indicatorClassName,
          )}
        />
      ) : null}
      <button
        type="button"
        role="tab"
        id={tabId(value)}
        aria-selected={active}
        aria-controls={panelId(value)}
        tabIndex={active ? 0 : -1}
        onClick={() => setValue(value)}
        className={cn(
          "relative z-10 inline-flex items-center justify-center whitespace-nowrap bg-transparent outline-none [&>svg]:shrink-0",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring focus-visible:ring-offset-2",
          triggerRingOffsetClasses[variant],
          "transition-colors",
          triggerSizeClasses[variant],
          active
            ? cn(activeLabelClasses[variant], "font-semibold")
            : "font-medium text-v2-muted-foreground hover:text-v2-foreground",
          radius,
          className,
        )}
      >
        {children}
      </button>
    </div>
  );
}

/**
 * One tab's panel.
 *
 * Every panel stays mounted and keeps the same element type across a switch, so
 * whatever a panel owns — a chart's isolated series, a scroll offset, a
 * half-filled field — survives a trip to another tab and back, and its content
 * is present in the server-rendered HTML for crawlers and assistive tech.
 * Inactive panels are hidden rather than swapped out, and deliberately omit
 * `role="tabpanel"` so the accessibility tree only ever exposes the live one.
 */
export function TabsContent({ value, children, className }: { value: string; children: ReactNode; className?: string }) {
  const { value: current, tabId, panelId } = useTabs();
  const reduce = useReducedMotion();
  const active = current === value;
  const offset = reduce ? 0 : 4;

  return (
    <motion.div
      hidden={!active}
      role={active ? "tabpanel" : undefined}
      id={panelId(value)}
      aria-labelledby={tabId(value)}
      tabIndex={active ? 0 : undefined}
      initial={{ opacity: 0, y: offset }}
      animate={{ opacity: active ? 1 : 0, y: active ? 0 : offset }}
      transition={{ duration: DURATION_QUICK, ease: EASE_OUT }}
      className={cn(
        "mt-4 outline-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v2-ring focus-visible:ring-offset-2 focus-visible:ring-offset-v2-background",
        className,
      )}
    >
      {children}
    </motion.div>
  );
}
